/**
 * PersonelIncelemePage.jsx — Personel Listesi (inceleme/admin tarafı), LİSTE
 * Route: /Management/Firmalar/Personel, /Management/Firmalar/Personel/:firmaId
 *
 * 2026-10-13 (backlog #357, backend #356): liste artık firma portalıyla AYNI sekmeli bileşen —
 * Components/Kisi/FirmaPersonelSekmeleri.jsx (Tüm Personeller [Tümü · Fuar Kadrosu · Görevlendirilmemiş · Onay Bekleyen] ·
 * Daimî · Geçici · İlişiği Kesilmişler · Tam Liste). Bu dosyada yalnızca denetmene özgü kısım kaldı: hangi firma/fuar
 * (adres + isteğe bağlı geçiş formu), firma seviyesi kart erişim uyarısı, kişi detayına ve belge karesinin gittiği
 * sayfaya yönlendirme. Eski iki alt sekme ("Fuar Kadrosu" tablosu-kartı ve "Daimî Liste" = FirmaRosterPanel.jsx) kalktı:
 * ikincisi firmanın TÜM kadrosuydu, yani Tüm Personeller'in kendisi. Personel Ekle, ilişki sonlandırma/geri alma,
 * "Fuardan Çıkar" ve fuara görevlendirme artık o bileşenin içinde. Denetmen Onayla/Reddet görmez (Eşleme'ye yalnızca
 * firma cevap verir; backend Saka'ya 403 döner) ve satırda kimin eşlediğini/cevabını fuar kadrosundaki kişiler için görür.
 *
 * Geçmiş (kaldırılan görünümlere ait olanlar kısaltıldı):
 *   · 2026-09-19 (#64): sekme adı "Personel Belgeleri" → "Personel Listesi"; Firma Portalı'nın kart dili.
 *   · 2026-08-11: belge inceleme (Onayla/Reddet/Beklemede'ye Al) etkileşimi PersonelDetayPage.jsx'te.
 *   · 2026-10-01 (#115): "Personel Ekle" tek modal (Components/Kisi/PersonelEkleModal), önce kimlik no sorgulanır.
 *
 * Varsayılan hedef firma 49858 / fuar 298 — Firma-tarafı `/Firma/Personel` sayfasının (`FirmaPersonelListPage.jsx`) aynı
 * seçili fuar/firma için gösterdiği listeyle BİREBİR aynı veriyi okur/yazar. Sayfa üstündeki mini formdan başka bir
 * firmaId/fuarId'ye geçilebilir (gömülü modda form yok).
 */
import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Input, Label, Button, Alert } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";
import { PageFrame, PageBody } from "../../../../Components/Common/EmbeddablePage";
import MainContext from "../../../../Components/Contexts/MainContext";
import { kisiSayfasiUrl } from "../../../../Components/Kisi/kisiBaglantilari";
import { PersonelLegendButton } from "../../../../Components/Kisi/PersonelKartParcalari";
import FirmaPersonelSekmeleri, { kadroSatiri } from "../../../../Components/Kisi/FirmaPersonelSekmeleri";
import { DEFAULT_FIRMA_ID, DEFAULT_FUAR_ID } from "../../../../services/personnelAdminService";
import { getFirmaCardAccessStatus, cardAccessModeMeta } from "../../../../services/cardAccessService";

/**
 * @param {boolean} embedded  true → sayfa kabuğu ve firmaId/fuarId geçiş formu basılmaz;
 *                            hedef firma prop'tan gelir. FirmaDetayPage.jsx'in
 *                            "Personel Listesi" sekmesi bu modda kullanılır.
 * @param {string}  firmaName gömülü modda üst sayfanın bildiği firma adı — "Personel Ekle"
 *                            penceresinde gösterilir; yoksa "#id" yazılır.
 */
export default function PersonelIncelemePage({
  embedded = false, firmaId: firmaIdProp, fuarId: fuarIdProp, firmaName = null,
}) {
  const { t: tx } = useTranslation("moduller");
  const { firmaId: firmaIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedFuarId } = useContext(MainContext);

  const firmaId = firmaIdProp ?? (firmaIdParam ? Number(firmaIdParam) : DEFAULT_FIRMA_ID);
  const fuarId = fuarIdProp ?? (Number(searchParams.get("fuarId")) || Number(selectedFuarId) || DEFAULT_FUAR_ID);

  const [firmaIdInput, setFirmaIdInput] = useState(String(firmaId));
  const [fuarIdInput, setFuarIdInput] = useState(String(fuarId));

  // Firma seviyesi kart erişimi (vergi no + bu fuarın istediği firma evrakları).
  const [cardAccess, setCardAccess] = useState(null);

  useEffect(() => { document.title = i18n.t("moduller:personelInceleme.personelListesiFds"); }, []);

  useEffect(() => {
    if (!firmaId || !fuarId) return undefined;
    let ignore = false;
    getFirmaCardAccessStatus(firmaId, fuarId)
      .then((data) => { if (!ignore) setCardAccess(data); })
      .catch(() => { /* sessizce düşer — sayfanın asıl verisi liste, bu ikincil */ });
    return () => { ignore = true; };
  }, [firmaId, fuarId]);

  function goTo(e) {
    e.preventDefault();
    // Gömülü modda firma/fuar geçiş alanları gizli. Bağımsız sayfada form Enter ile de gönderilir.
    if (embedded) return;
    const id = firmaIdInput.trim();
    if (!id) return;
    navigate(`/Management/Firmalar/Personel/${id}?fuarId=${fuarIdInput.trim() || DEFAULT_FUAR_ID}`);
  }

  // Detay sayfasının "Dön" bağlantısı geldiği yere dönsün (#262): Firma Detay › Personel sekmesinden
  // gelen, bağımsız ekip sayfasına değil o sekmeye geri gitmeli. State yoksa detay kendi varsayılanına düşer.
  const nereden = {
    from: `${location.pathname}${location.search}`,
    fromLabel: embedded ? tx("personelInceleme.firmaDetayinaDon") : tx("personelInceleme.personelListesineDon"),
  };

  // Fuar kadrosundaki kişi firmalı detay sayfasında (belge incelemesi, Eşleme kartı) açılır; kadroda olmayan kişi — o sayfa
  // seçili fuarın kadrosunu okuyup 404 verirdi — firmasız Kişi sayfasında (#292) açılır.
  const detayOf = (row) => {
    const kadroda = kadroSatiri(row);
    const href = kisiSayfasiUrl({
      firmaId: kadroda ? firmaId : null, personId: row.personId, fuarId, geri: nereden.from, geriEtiket: nereden.fromLabel,
    });
    const git = kadroda
      ? () => navigate(`/Management/Firmalar/Personel/${firmaId}/${row.personId}?fuarId=${fuarId}`, { state: nereden })
      : () => navigate(href);
    return { href, onGit: git };
  };

  // Belge karesine tıklamak kişinin detayındaki ilgili karta odaklanır; "Firma İSG Belgeleri" firma çapında tek kayıt
  // olduğu için Firma Detay'ın Profil & Belgeler sekmesine gider.
  function belgeGit(row, docKey) {
    if (docKey === "firmaBelgeleri") {
      navigate(`/Management/Firmalar/Detay/${firmaId}?fuarId=${fuarId}&tab=profil&profilTab=belgeler`);
    } else {
      navigate(`/Management/Firmalar/Personel/${firmaId}/${row.personId}?fuarId=${fuarId}&belge=${docKey}`, { state: nereden });
    }
  }

  // #622: same as the company portal's list - a foreign company's temporary-access hint has no tax number in it.
  const erisimMeta = cardAccess && cardAccess.cardAccessMode !== "FULL"
    ? cardAccessModeMeta(cardAccess.cardAccessMode, { yabanci: cardAccess.taxNumberGerekli === false })
    : null;

  return (
    <PageFrame embedded={embedded}>
      <PageBody embedded={embedded} title={tx("personelInceleme.personelListesi")} pageTitle={tx("personelInceleme.firmalar")}>

        <div className="d-flex align-items-center gap-2 mb-3">
          {/* Firma/fuar geçişi yalnızca bağımsız sayfada anlamlı — gömülü modda hedef firma zaten FirmaDetayPage'in URL'inden geliyor. */}
          {!embedded && (
            <form className="d-flex align-items-end gap-2" onSubmit={goTo}>
              <div>
                <Label className="small text-muted mb-1">{tx("personelInceleme.firmaId")}</Label>
                <Input bsSize="sm" style={{ width: 140 }} value={firmaIdInput} onChange={(e) => setFirmaIdInput(e.target.value)} />
              </div>
              <div>
                <Label className="small text-muted mb-1">{tx("personelInceleme.fuarId")}</Label>
                <Input bsSize="sm" style={{ width: 120 }} value={fuarIdInput} onChange={(e) => setFuarIdInput(e.target.value)} />
              </div>
              <Button color="primary" size="sm" type="submit">{tx("common:goruntule")}</Button>
            </form>
          )}
          {/* Backlog #107: kart üzerindeki kare/rozet/sayaçların ne anlama geldiğini açıklar. */}
          <PersonelLegendButton className="ms-auto" />
        </div>

        {/* Firma seviyesi engel — kişi başına rozet tek başına yetmez, firmanın vergi no / fuar evrakı eksikse kimseye
            kart çıkmaz. Düzenleme Profil & Belgeler'de. */}
        {erisimMeta && (
          <Alert fade={false} color={erisimMeta.color} className="small">
            <span className="fw-semibold">{erisimMeta.label}:</span>{" "}
            {erisimMeta.hint}
          </Alert>
        )}

        {!embedded && (
        <div className="text-muted small mb-2">
          {tx("personelInceleme.firmaIdFuarBuRoster", { firmaId, fuarId })}{" "}
          <a href={`/Management/Firmalar/Detay/${firmaId}?fuarId=${fuarId}`}>{tx("personelInceleme.firmaDetay")}</a>{" "}
          {tx("personelInceleme.sayfasindakiFirmaGeneliBelgelerleAyni")}
        </div>
        )}

        <FirmaPersonelSekmeleri
          firmaId={firmaId}
          firmaAdi={firmaName}
          fuarId={fuarId}
          taraf="denetmen"
          yazabilir
          baslangicSekme={searchParams.get("sekme") || undefined}
          baslangicAltSekme={searchParams.get("alt") || undefined}
          detayOf={detayOf}
          belgeGit={belgeGit}
        />
      </PageBody>
    </PageFrame>
  );
}
